import React, { useState, useEffect } from 'react'
import { EllipsisVerticalIcon, ArrowLeftIcon, TicketIcon, EyeIcon, PencilIcon } from '@heroicons/react/16/solid'
import { t } from 'i18next'
import {
    Dropdown,
    DropdownButton,
    DropdownDivider,
    DropdownItem,
    DropdownLabel,
    DropdownMenu,
} from 'components/dropdown'
import { Divider } from 'components/divider'
import { useLazyQuery } from '@apollo/client'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/table'
import { ErrorDialog, SuccessDialog } from 'components/custom/ResponseDialog'
import { GET_BOAT_DAY_PASS_PRICE } from 'graphql/booking/queries'
import loadingImage from '../../assets/images/boat-loading.gif'
import { customDateFormat } from 'utils/common'
import { useNavigate } from 'react-router-dom'
import { Pagination, PaginationList, PaginationNext, PaginationPage, PaginationPrevious } from 'components/pagination'
import { getPageRange } from 'utils/paginationHelper'
import { GET_CHARTER_BOAT_CONFIGS_LIST } from 'graphql/charterBoats/queries'
import { ImageDisplay } from 'components/custom/ImageDisplay'
import { Badge } from 'components'
import { BoatIcon } from 'components/icons'

export default function ManageCharterBoat() {
    const navigate = useNavigate()
    // Dialog Message
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [showSucDialog, setShowSucDialog] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [successMessage, setSuccessMessage] = useState('')

    const [passPriceList, setPassPriceList] = useState(null)
    const [passPriceListCount, setPassPriceListCount] = useState(0)

    // pagination
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const totalPages = Math.ceil(passPriceListCount / rowsPerPage)

    const getStatusBadge = discountStatus => {
        const statusMap = {
            ACT: { color: 'emerald', label: 'Active' },
            DRAFT: { color: 'zinc', label: 'Draft' },
            EXP: { color: 'amber', label: 'Expired' },
            INA: { color: 'rose', label: 'Inactive' },
        }
        const status = statusMap[discountStatus] || { color: 'zinc', label: discountStatus }
        return <Badge color={status.color}>{t(status.label)}</Badge>
    }

    const [getPassPriceList, { loading: passPriceListLoading, error: passPriceListError }] = useLazyQuery(
        GET_CHARTER_BOAT_CONFIGS_LIST,
        {
            fetchPolicy: 'network-only',
            notifyOnNetworkStatusChange: true,
            onCompleted: data => {
                if (data.getCharterBoatConfigs.success && data.getCharterBoatConfigs.code === '200') {
                    setPassPriceList(data.getCharterBoatConfigs.charter_boat_configs)
                    setPassPriceListCount(data.getCharterBoatConfigs.total_count)
                }
            },
            onError: error => {
                setErrorMessage(error.message)
                setShowErrDialog(true)
            },
        }
    )

    // Search boats Boat List
    const searchBoats = () => {
        getPassPriceList({
            variables: {
                page: {
                    limit: rowsPerPage,
                    offset: page * rowsPerPage,
                },
                filter: {
                    to_date: null,
                    status_code: null,
                    from_date: null,
                    boat_id: null,
                },
            },
        })
    }

    useEffect(() => {
        searchBoats()
    }, [page, rowsPerPage])

    const handlePageChange = newPage => {
        setPage(newPage)
    }
    const handleDialogClose = () => {
        setShowErrDialog(false)
        setErrorMessage('')
        setShowSucDialog(false)
        setSuccessMessage('')
    }
    return (
        <>
            <div className="mb-6 flex justify-between items-center">
                <button
                    onClick={() => {
                        navigate('/')
                    }}
                    type="button"
                    className="inline-flex items-center gap-x-1.5 rounded-md text-sm"
                >
                    <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-5 rtl:rotate-180" />
                    {t('Home')}
                </button>
            </div>

            <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="max-sm:w-full sm:flex-1">
                    <div>
                        <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap justify-between">
                            <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap">
                                <h1 className="text-base font-semibold leading-7 text-gray-900 flex items-center">
                                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10 bg-[#e9eff1] w-[40px] h-[40px]">
                                        <BoatIcon className="w-[20px] h-[20px] fill-[#285f74]" />
                                    </div>
                                    <div>
                                        <span className="ms-3"> {t('Charter Boat Price')}</span>
                                        <p className="ms-3 text-sm font-normal leading-6 weight-[200] text-gray-500">
                                            {t('Easily configure the charter boat validity and price.')}
                                        </p>
                                    </div>
                                </h1>
                            </div>
                            <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap sm:text-end">
                                <button
                                    onClick={() => navigate('/price-config/charter-boat-price/add')}
                                    type="button"
                                    className="hover:bg-primary/[.9] bg-primary focus-visible:outline-primary justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                                >
                                    {t('Add Price')}
                                </button>
                            </div>
                        </div>
                    </div>
                    {/* <div className="mt-8 mb-4 flex flex-col sm:flex-row flex-wrap max-w-xl gap-4">
                        <div className="flex-2">
                            <InputGroup>
                                <MagnifyingGlassIcon />
                                <Input name="search" id="search" placeholder="Search locations&hellip;" />
                            </InputGroup>
                        </div>

                        <div className="flex gap-2 justify-end">
                            <button
                                type="button"
                                className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/[0.9]"
                            >
                                {t('Search')}
                            </button>
                            <button
                                type="button"
                                className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                            >
                                {t('Clear')}
                            </button>
                        </div>
                    </div> */}
                </div>
            </div>
            {passPriceListLoading && (
                <div className="flex flex-col justify-center items-center bg-white h-96 mt-16">
                    <img alt="logo" src={loadingImage} className="w-[250px]" />
                    <p className="pt-3 text-primary font-semibold">{t('Loading, please wait')}...</p>
                </div>
            )}
            {passPriceListError && (
                <div className="flex justify-center items-center bg-white py-5">
                    <div className="text-lg text-center">{t('Failed to fetch banner!')}</div>
                </div>
            )}
            {!passPriceListLoading && !passPriceListError && passPriceList?.length === 0 && (
                <div className="text-center py-24 bg-gray-50 mt-8 rounded-lg">
                    <svg
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        className="mx-auto h-12 w-12 text-gray-400"
                    >
                        <path
                            d="M16.6725 16.6412L21 21M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
                            strokeWidth={2}
                            vectorEffect="non-scaling-stroke"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    <h3 className="mt-2 text-sm font-semibold text-gray-900">{t('No data found!')}</h3>
                    <p className="mt-1 text-sm text-gray-500">{t('Try adjusting your search criteria')}</p>
                    <div className="mt-6"></div>
                </div>
            )}
            {!passPriceListLoading && !passPriceListError && passPriceList?.length > 0 && (
                <>
                    <Divider className="mt-5" />
                    <div className="table-custom overflow-x-scroll lg:overflow-hidden rounded-b-lg bg-white p-0 flex ">
                        <Table className="[--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)]">
                            <TableHead>
                                <TableRow>
                                    <TableHeader>{t('Name')}</TableHeader>
                                    <TableHeader>{t('Charter Boat Image')}</TableHeader>
                                    <TableHeader>{t('Start Date')}</TableHeader>
                                    <TableHeader>{t('End Date')}</TableHeader>
                                    <TableHeader>{t('Status')}</TableHeader>
                                    <TableHeader>{t('Actions')}</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {passPriceList.map(pass => (
                                    <TableRow
                                        key={pass.boat_id}
                                        className="cursor-pointer hover:bg-gray-50"
                                        onClick={() =>
                                            navigate(`/price-config/charter-boat-price/${pass.boat_id}`, {
                                                state: { configId: pass.config_id },
                                            })
                                        }
                                        title={`Location #${pass.boat_id}`}
                                    >
                                        <TableCell>
                                            <div className="font-medium text-gray-900">
                                                {pass?.boat?.name?.en || '-'}
                                            </div>
                                            {pass?.boat?.name?.en && (
                                                <div className="text-sm text-gray-500 mt-1 line-clamp-2">
                                                    {pass?.boat?.name?.en}
                                                </div>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex gap-2 items-center">
                                                {pass?.boat?.details?.images?.[0]?.s3_key ? (
                                                    <ImageDisplay
                                                        s3Key={pass.boat?.details?.images?.[0]?.s3_key}
                                                        file=""
                                                        classStyle="w-[180px] h-[50px] object-cover rounded-md border-[1px] border-primary/[.3]"
                                                    />
                                                ) : (
                                                    <div className="text-sm text-gray-400">No Image</div>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell>{customDateFormat(pass.from_date)}</TableCell>
                                        <TableCell>{customDateFormat(pass.to_date)}</TableCell>
                                        <TableCell>{getStatusBadge(pass.status?.code)}</TableCell>
                                        <TableCell>
                                            <Dropdown>
                                                <DropdownButton
                                                    plain
                                                    aria-label="More options"
                                                    onClick={e => e.stopPropagation()}
                                                    className="cursor-pointer"
                                                >
                                                    <EllipsisVerticalIcon />
                                                </DropdownButton>
                                                <DropdownMenu anchor="bottom">
                                                    <DropdownItem
                                                        onClick={e => {
                                                            e.stopPropagation()
                                                            navigate(
                                                                `/price-config/charter-boat-price/${pass.boat_id}`,
                                                                { state: { configId: pass.config_id } }
                                                            )
                                                        }}
                                                    >
                                                        <EyeIcon />
                                                        <DropdownLabel>{t('View')}</DropdownLabel>
                                                    </DropdownItem>
                                                    <>
                                                        <DropdownDivider />
                                                        <DropdownItem
                                                            onClick={e => {
                                                                e.stopPropagation()
                                                                navigate(
                                                                    `/price-config/charter-boat-price/${pass.boat_id}`,
                                                                    { state: { configId: pass.config_id } }
                                                                )
                                                            }}
                                                        >
                                                            <PencilIcon />
                                                            <DropdownLabel>{t('Edit')}</DropdownLabel>
                                                        </DropdownItem>
                                                    </>
                                                </DropdownMenu>
                                            </Dropdown>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                    <Divider />
                    <Pagination className="py-6">
                        <PaginationPrevious
                            onClick={() => handlePageChange(page > 0 ? page - 1 : 0)}
                            disabled={page === 0}
                        />
                        <PaginationList>
                            {getPageRange(page, totalPages).map(index => (
                                <PaginationPage
                                    key={index}
                                    current={index === page}
                                    onClick={() => handlePageChange(index)}
                                >
                                    {index + 1}
                                </PaginationPage>
                            ))}
                        </PaginationList>
                        <PaginationNext
                            onClick={() => handlePageChange(page < totalPages - 1 ? page + 1 : totalPages - 1)}
                            disabled={page === totalPages - 1}
                        />
                    </Pagination>
                    <Divider />
                </>
            )}
            {showErrDialog && (
                <ErrorDialog status={true} title="Error" message={errorMessage} onClose={handleDialogClose} />
            )}
            {showSucDialog && (
                <SuccessDialog status={true} title="Success" message={successMessage} onClose={handleDialogClose} />
            )}
        </>
    )
}
